import { ApolloError } from '@apollo/client'
import { useLazyQuery } from '@apollo/client/react/hooks/useLazyQuery'
import { summarizeApolloError } from '@deltek/specification-client/core/Utilities/apolloUtils'
import { useCallback } from 'react'

import { User } from '@deltek/specification-client/core/Models/user'
import { GetProjectTeamQuery } from 'api/graphQL/queries/project.queries'
import { GetAllUsersQuery } from 'api/graphQL/queries/user.queries'
import { AllProjectResponse, Assignment } from 'Models/project'
import { AllUsersResponse } from 'Models/user'
import { useProjectPropertiesStore } from '../ProjectPropertiesStore'
import { AssignedUserList } from '../types/types'

/**
 * Interface representing the properties required for Project Data API.
 */
export interface ProjectPropertiesApiProps {
    projectId: string
    contentTeamFromStudioPage: boolean
}

/**
 * A custom hook that provides functionality to fetch and manage project data.
 *
 * @template T - The type of the props that extend `ProjectDataApiProps`.
 * @param {T} props - The properties required to initialize the hook.
 * @param {string} props.projectId - The ID of the project to fetch data for.
 * @returns {Object} An object containing the `fetchProjectData` function.
 * @returns {Function} returns.fetchProjectData - A function to fetch the project data.
 */
export const useProjectPropertiesQuery = <T extends ProjectPropertiesApiProps>({
    projectId,
    contentTeamFromStudioPage,
}: T) => {
    const setAssignedUsersList = useProjectPropertiesStore(
        (s) => s.setAssignedUsersList
    )
    const handleFetchError = (error: ApolloError) => {
        const summarizedError = summarizeApolloError(
            error,
            'WorkspaceEditor-getProjectDataQuery'
        )
        console.error(summarizedError)
    }

    const [getProjectTeamData] = useLazyQuery<AllProjectResponse>(
        GetProjectTeamQuery,
        {
            onCompleted: (projectData) => {
                if (projectData) {
                    let usersList: AssignedUserList[] = []
                    projectData.getProject.projectTeam?.assignments?.forEach(
                        (assignedUser: Partial<Assignment>) => {
                            const obj = {
                                userName:
                                    assignedUser.user?.profileInfo?.firstName +
                                    ' ' +
                                    assignedUser.user?.profileInfo?.lastName,
                                userId: assignedUser.user?.id,
                            }
                            usersList.push(obj)
                        }
                    )
                    usersList.push({
                        userName: 'Unassigned',
                        userId: null,
                    })
                    usersList = usersList.sort(
                        (a, b) => -b.userName.localeCompare(a.userName)
                    )
                    setAssignedUsersList(usersList)
                }
            },
            onError: handleFetchError,
        }
    )

    const [getAllUsers] = useLazyQuery<AllUsersResponse>(GetAllUsersQuery, {
        fetchPolicy: 'no-cache',
        onCompleted: (data) => {
            if (data) {
                let usersList: AssignedUserList[] = []
                data.getAllUsers.users.forEach(
                    (assignedUser: Partial<User>) => {
                        const obj = {
                            userName:
                                assignedUser?.profileInfo?.firstName +
                                ' ' +
                                assignedUser?.profileInfo?.lastName,
                            userId: assignedUser?.id,
                        }
                        usersList.push(obj)
                    }
                )
                usersList.push({
                    userName: 'Unassigned',
                    userId: null,
                })
                usersList = usersList.sort(
                    (a, b) => -b.userName.localeCompare(a.userName)
                )
                setAssignedUsersList(usersList)
            }
        },
        onError: handleFetchError,
    })

    const fetchAssignedUserList = useCallback(async (): Promise<void> => {
        if (contentTeamFromStudioPage) {
            getAllUsers({})
        } else {
            getProjectTeamData({
                variables: {
                    projectId: projectId,
                },
            })
        }
    }, [projectId, contentTeamFromStudioPage])

    return { fetchAssignedUserList }
}
